import { Box } from '@mentimeter/ragnar-ui/box';
import { scaleOrdinal } from 'd3-scale';
import { pie } from 'd3-shape';
import { useState } from 'react';
import type {
  ChoicesData,
  ChoicesStaticOptions,
  ChoicesVisualization,
} from '../types';
import { getPercentageGetter } from '../utils/getPercentageGetter';
import { SvgWithFont } from '../utils/SvgWithFont';
import type { MultipleChoicePieChartRenderChoiceLegend } from '../utils/types/render-options-by-slide-type/multiple-choice';
import type { RenderChoiceLabel } from '../utils/types/render-options-by-slide-type/types';
import { PieArc } from './PieArc';
import { PieLegend } from './PieLegend';
import { getOpacity } from './utils';

const DONUT_VARIANT_INNER_RADIUS_PERCENTAGE = 0.5;
const PLACEHOLDER_OPACITY = 0.5;

interface PieVisualizationProps extends ChoicesVisualization {
  isDonut?: boolean;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
  renderChoiceLegend?: MultipleChoicePieChartRenderChoiceLegend | undefined;
}

export function PieVisualization({
  data,
  staticOptions,
  width,
  height,
  colors,
  textColor,
  shouldShowCorrect = false,
  shouldShowScorePercentage = false,
  isDonut = false,
  disableAnimations = false,
  keyAccessor = (d) => String(d.id),
  labelAccessor = (d) => d.label,
  valueAccessor = (d) => d.score,
  correctAnswerAccessor = (d) => d.isMarkedCorrect,
  identities,
  onClickOption,
  fontFamily,
  renderChoiceLegend,
  renderChoiceLabel,
}: PieVisualizationProps) {
  const hasData = data.some((d) => valueAccessor(d) !== 0);

  const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);

  const rootWidth = Math.max(width, 0);
  const rootHeight = Math.max(height, 0);

  const legendWidth = rootWidth / 2;
  const pieWidth = rootWidth * 0.8 - legendWidth;

  if (legendWidth <= 0 || pieWidth <= 0) {
    return null;
  }

  const pieGenerator = pie<ChoicesData>()
    .value(valueAccessor)
    .sort(null)
    .padAngle(0.016);

  const pieArcs = pieGenerator(data.filter((d) => valueAccessor(d) > 0)).filter(
    (d): d is NonNullable<typeof d> => d !== null,
  );

  const numberOfVisibleArcs = pieArcs.length;

  const radius = Math.min(pieWidth, rootHeight) / 2;
  const minimumInnerRadius =
    numberOfVisibleArcs === 1 ? 0 : Math.sqrt(numberOfVisibleArcs);
  const innerRadius = isDonut
    ? radius * DONUT_VARIANT_INNER_RADIUS_PERCENTAGE
    : minimumInnerRadius;

  const discreteValues = staticOptions.map(keyAccessor);
  const getOptionColor = scaleOrdinal<string>(colors).domain(discreteValues);

  const getScoreDisplayText = (score: number): string => {
    if (shouldShowScorePercentage) {
      const getPercentage = getPercentageGetter(
        shouldShowScorePercentage.percentOf,
      );
      return getPercentage(score).toFixed(0) + '%';
    } else {
      return score.toString();
    }
  };

  return (
    <Box
      data-testid={isDonut ? 'donut-visualization' : 'pie-visualization'}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      gap="clamp(40px, 5%, 80px)"
    >
      <SvgWithFont
        width={pieWidth}
        height={rootHeight}
        fontFamily={fontFamily}
        aria-hidden
      >
        <g
          data-name="bounds"
          width="100%"
          height="100%"
          transform={`translate(${pieWidth - radius}, ${rootHeight / 2})`}
        >
          {!hasData && (
            <g data-testid="pie-placeholder">
              <mask id="inner-circle">
                <rect
                  x={-radius}
                  y={-radius}
                  width="100%"
                  height="100%"
                  fill={textColor}
                />
                <circle r={innerRadius} fill="black" />
              </mask>

              <circle
                r={radius}
                fill={textColor}
                fillOpacity={PLACEHOLDER_OPACITY}
                mask="url(#inner-circle)"
              />
            </g>
          )}
          {hasData &&
            pieArcs.map((arc) => {
              const color = getOptionColor(keyAccessor(arc.data));
              const value = valueAccessor(arc.data);

              const staticOption =
                staticOptions.find(
                  (option) => keyAccessor(option) === keyAccessor(arc.data),
                ) ?? ({ isMarkedCorrect: false } as ChoicesStaticOptions);

              const opacity = getOpacity({
                hoveredItemKey,
                currentItemKey: keyAccessor(arc.data),
                isIncorrect:
                  shouldShowCorrect && !correctAnswerAccessor(staticOption),
              });

              return (
                value > 0 && (
                  <g key={keyAccessor(arc.data)} data-testid="pie-arc-group">
                    <PieArc
                      color={color}
                      arcInfo={arc}
                      opacity={opacity}
                      innerRadius={innerRadius}
                      outerRadius={radius}
                      disableAnimations={disableAnimations}
                      onMouseEnter={() =>
                        setHoveredItemKey(keyAccessor(arc.data))
                      }
                      onMouseLeave={() => setHoveredItemKey(null)}
                      onClick={
                        onClickOption && (() => onClickOption(arc.data.id))
                      }
                    >
                      {getScoreDisplayText(value)}
                    </PieArc>
                  </g>
                )
              );
            })}
        </g>
      </SvgWithFont>
      <PieLegend
        shouldShowScores={hasData}
        items={staticOptions.map((staticOption) => {
          const optionData =
            data.find(
              (data) => keyAccessor(data) === keyAccessor(staticOption),
            ) ?? ({ score: 0 } as ChoicesData);

          const value = valueAccessor(optionData);

          const displayValue = getScoreDisplayText(value);

          return {
            ...staticOption,
            value,
            displayValue,
          };
        })}
        keyAccessor={keyAccessor}
        labelAccessor={labelAccessor}
        correctAnswerAccessor={correctAnswerAccessor}
        getOptionColor={getOptionColor}
        legendWidth={legendWidth}
        boundedHeight={rootHeight}
        hoveredItemKey={hoveredItemKey}
        setHoveredItemKey={setHoveredItemKey}
        disableAnimations={disableAnimations}
        shouldShowCorrect={shouldShowCorrect}
        identities={identities}
        onClickOption={onClickOption}
        renderChoiceLabel={renderChoiceLabel}
        renderChoiceLegend={renderChoiceLegend}
      />
    </Box>
  );
}
